// Unified cosmetics picker — browse + buy + equip across 3 categories:
// card-back skins, victory animations, and avatar frames.
//
// Replaces the older single-category SkinPicker. Keeps the same data-testid
// `skin-picker-modal` for compatibility, plus adds `cosmetics-modal` alias.
import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CardBack } from "./CardBack";
import AvatarFrame from "./AvatarFrame";
import { fetchCosmeticsCatalog, buyCosmetic, equipCosmetic } from "../lib/cosmetics";
import { sfx } from "../lib/sound";
import { getInitials } from "../lib/avatar";

const TABS = [
  { key: "skins",         label: "Card Backs",  catalogKey: "skins",         kind: "skin" },
  { key: "victory_anims", label: "Victory FX",  catalogKey: "victory_anims", kind: "victory_anim" },
  { key: "avatar_frames", label: "Avatar Frame", catalogKey: "avatar_frames", kind: "avatar_frame" },
];

export default function CosmeticsModal({ open, profile, onClose, onApplied, initialTab = "skins" }) {
  const [tab, setTab] = useState(initialTab);
  const [catalog, setCatalog] = useState({ skins: [], victory_anims: [], avatar_frames: [] });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const [confirmingBuy, setConfirmingBuy] = useState(null); // item awaiting confirm-buy
  const refresh = useCallback(async () => {
    setLoading(true);
    try { setCatalog(await fetchCosmeticsCatalog()); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (open) refresh(); }, [open, refresh]);
  useEffect(() => { if (open) setTab(initialTab); }, [open, initialTab]);

  if (!open) return null;

  const items = catalog[tab] || [];
  const activeTab = TABS.find((t) => t.key === tab);

  const onTileClick = async (it) => {
    if (busyId) return;
    if (!it.owned) {
      // Need to buy first — show confirm panel
      setConfirmingBuy(it);
      setError("");
      return;
    }
    if (it.active) return; // already equipped
    setBusyId(it.id); setError("");
    try {
      const updated = await equipCosmetic(activeTab.kind, it.id);
      sfx.click();
      onApplied?.(updated);
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to equip.");
    } finally {
      setBusyId(null);
    }
  };

  const confirmBuy = async () => {
    if (!confirmingBuy || busyId) return;
    const it = confirmingBuy;
    setBusyId(it.id); setError("");
    try {
      const res = await buyCosmetic(it.id);
      sfx.win();
      // Optimistic profile gem update for the parent (Lobby header)
      if (res?.gems != null && profile) onApplied?.({ ...profile, gems: res.gems });
      // Auto-equip after purchase
      const updated = await equipCosmetic(activeTab.kind, it.id);
      onApplied?.(updated);
      await refresh();
      setConfirmingBuy(null);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Purchase failed.";
      setError(detail);
      // If it's a 'not enough gems' error, leave the confirm panel visible
      if (!detail.toLowerCase().includes("gem")) setConfirmingBuy(null);
    } finally {
      setBusyId(null);
    }
  };

  const renderPreview = (it, small = false) => {
    const w = small ? 56 : 72;
    const h = small ? 76 : 96;
    if (activeTab.kind === "skin") {
      return <div style={{ width: w, height: h }}><CardBack skin={it.id} color="#FFD700" suit="♣" size={small ? "sm" : "md"} /></div>;
    }
    if (activeTab.kind === "victory_anim") {
      return (
        <div className="flex items-center justify-center rounded-lg" style={{
          width: w, height: h,
          background: "linear-gradient(135deg, rgba(255,215,0,0.10), rgba(0,229,255,0.08))",
          border: "1px solid rgba(255,255,255,0.10)",
        }}>
          <span style={{ fontSize: 30 }}>{
            it.id === "fireworks" ? "🎆" : it.id === "lightning" ? "⚡" : "🎉"
          }</span>
        </div>
      );
    }
    // avatar_frame
    const initials = getInitials(profile?.name || "P");
    return (
      <div className="flex items-center justify-center" style={{ width: w, height: h }}>
        <AvatarFrame frame={it.id} size={small ? 44 : 56}>
          <div className="flex items-center justify-center w-full h-full font-display font-black"
            style={{ background: "linear-gradient(155deg, #FFD700, #00E5FF, #B537F2)", color: "#0B0C10",
                     fontSize: small ? 14 : 17, letterSpacing: 0.5 }}>
            {initials}
          </div>
        </AvatarFrame>
      </div>
    );
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[110] flex items-center justify-center px-4"
        data-testid="cosmetics-modal"
      >
        <div className="absolute inset-0" style={{ background: "rgba(5,5,7,0.78)", backdropFilter: "blur(14px)" }} onClick={onClose} />
        <motion.div
          initial={{ y: 12, opacity: 0, scale: 0.96 }} animate={{ y: 0, opacity: 1, scale: 1 }}
          className="relative glass rounded-3xl p-5 md:p-6 w-full"
          style={{ border: "1px solid rgba(255,215,0,0.4)", maxWidth: 540, maxHeight: "90vh", overflowY: "auto" }}
          data-testid="skin-picker-modal"
        >
          <div className="flex items-center justify-between mb-3 gap-2">
            <span className="label-eyebrow" style={{ color: "#FFD700" }}>COSMETICS</span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-white/65">
                ◈ <b>{(profile?.gems ?? 0).toLocaleString()}</b>
              </span>
              <button onClick={onClose} className="text-white/55 hover:text-white text-xs underline" data-testid="cosmetics-close">Close</button>
            </div>
          </div>
          <h2 className="font-display font-black text-xl md:text-2xl mb-3">Customize your look</h2>

          {/* Tab bar */}
          <div className="flex gap-1.5 mb-4 overflow-x-auto" data-testid="cosmetics-tabs">
            {TABS.map((t) => {
              const isActive = t.key === tab;
              return (
                <button
                  key={t.key}
                  onClick={() => { setTab(t.key); setConfirmingBuy(null); setError(""); }}
                  className="rounded-full px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider whitespace-nowrap"
                  style={{
                    background: isActive ? "rgba(255,215,0,0.15)" : "rgba(255,255,255,0.05)",
                    border: `1px solid ${isActive ? "rgba(255,215,0,0.55)" : "rgba(255,255,255,0.10)"}`,
                    color: isActive ? "#FFD700" : "rgba(255,255,255,0.7)",
                    cursor: "pointer",
                  }}
                  data-testid={`cosmetics-tab-${t.key}`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Confirm-buy panel */}
          {confirmingBuy && (
            <motion.div
              initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
              className="rounded-2xl p-3 mb-4 flex items-center gap-3"
              style={{
                background: confirmingBuy.featured ? "rgba(255,80,140,0.10)" : "rgba(255,215,0,0.10)",
                border: `1px solid ${confirmingBuy.featured ? "rgba(255,80,140,0.65)" : "rgba(255,215,0,0.45)"}`,
              }}
              data-testid="cosmetics-confirm-buy"
            >
              {renderPreview(confirmingBuy, true)}
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-sm truncate flex items-center gap-2">
                  <span className="truncate">{confirmingBuy.name}</span>
                  {confirmingBuy.featured && (
                    <span className="font-display font-black flex-shrink-0"
                      style={{
                        fontSize: 9, letterSpacing: "0.08em",
                        background: "linear-gradient(135deg, #FF2A6B, #B537F2)",
                        color: "#fff", padding: "2px 6px", borderRadius: 999,
                      }}>
                      DAILY −{confirmingBuy.discount_pct}%
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-white/65 mt-0.5 truncate">{confirmingBuy.description}</div>
                {error && <div className="text-[11px] mt-1" style={{ color: "#FF8A8A" }}>{error}</div>}
              </div>
              <div className="flex flex-col gap-1.5 flex-shrink-0">
                <button
                  onClick={confirmBuy}
                  disabled={busyId === confirmingBuy.id}
                  className="rounded-full px-3 py-1.5 font-display font-bold text-xs"
                  style={{
                    background: confirmingBuy.featured ? "linear-gradient(135deg, #FF6BAA, #B537F2)" : "#FFD700",
                    color: confirmingBuy.featured ? "#fff" : "#0B0C10",
                    opacity: busyId ? 0.6 : 1, minWidth: 84,
                  }}
                  data-testid="cosmetics-buy-confirm"
                >
                  {busyId === confirmingBuy.id ? "…" : `Buy ◈ ${confirmingBuy.price_gems}`}
                </button>
                {confirmingBuy.featured && (
                  <div className="text-[9px] text-white/55 text-center line-through">
                    was ◈ {confirmingBuy.original_price_gems}
                  </div>
                )}
                <button
                  onClick={() => { setConfirmingBuy(null); setError(""); }}
                  disabled={busyId === confirmingBuy.id}
                  className="text-[11px] text-white/55 hover:text-white underline"
                  data-testid="cosmetics-buy-cancel"
                >Cancel</button>
              </div>
            </motion.div>
          )}

          {!confirmingBuy && error && (
            <div className="text-xs mb-3" style={{ color: "#FF8A8A" }} data-testid="cosmetics-error">{error}</div>
          )}

          {/* Item grid */}
          <div className="grid grid-cols-3 gap-2.5" data-testid="cosmetics-grid">
            {loading && items.length === 0 ? (
              // Loading skeleton — 6 dimmed placeholders so the modal looks alive
              [0,1,2,3,4,5].map((i) => (
                <div
                  key={`skel-${i}`}
                  className="rounded-2xl p-2 flex flex-col items-center gap-1.5"
                  style={{
                    background: "rgba(255,255,255,0.03)",
                    border: "1.5px solid rgba(255,255,255,0.06)",
                    minHeight: 130,
                  }}
                  data-testid="cosmetics-skeleton"
                >
                  <div style={{
                    width: 72, height: 96, borderRadius: 10,
                    background: "linear-gradient(110deg, rgba(255,255,255,0.04) 30%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.04) 70%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.4s ease-in-out infinite",
                  }} />
                  <div style={{
                    width: 50, height: 9, borderRadius: 4,
                    background: "rgba(255,255,255,0.08)",
                  }} />
                  <div style={{
                    width: 30, height: 8, borderRadius: 4,
                    background: "rgba(255,255,255,0.05)",
                  }} />
                </div>
              ))
            ) : items.length === 0 ? (
              <div className="col-span-3 text-center text-white/55 text-sm py-8" data-testid="cosmetics-empty">
                No items in this category yet.
              </div>
            ) : items.map((it) => {
              const tileBg = it.active ? "rgba(255,215,0,0.12)" :
                             it.featured ? "rgba(255,80,140,0.10)" :
                             it.owned  ? "rgba(255,255,255,0.05)" :
                                         "rgba(255,255,255,0.02)";
              const tileBorder = it.active ? "rgba(255,215,0,0.7)" :
                                 it.featured ? "rgba(255,80,140,0.65)" :
                                 it.owned  ? "rgba(255,255,255,0.10)" :
                                             "rgba(0,229,255,0.30)";
              return (
                <button
                  key={it.id}
                  onClick={() => onTileClick(it)}
                  disabled={busyId === it.id}
                  className="rounded-2xl p-2 flex flex-col items-center gap-1.5 relative"
                  style={{
                    background: tileBg,
                    border: `1.5px solid ${tileBorder}`,
                    cursor: busyId ? "not-allowed" : "pointer",
                    opacity: busyId === it.id ? 0.6 : 1,
                  }}
                  data-testid={`cosmetic-tile-${it.id}`}
                >
                  {it.featured && !it.owned && (
                    <span
                      className="absolute font-display font-black"
                      style={{
                        top: -7, right: -4,
                        fontSize: 8, letterSpacing: "0.08em",
                        background: "linear-gradient(135deg, #FF2A6B, #B537F2)",
                        color: "#fff", padding: "2px 6px", borderRadius: 999,
                        boxShadow: "0 0 10px rgba(255,80,140,0.6)",
                        zIndex: 2,
                      }}
                      data-testid={`cosmetic-featured-${it.id}`}
                    >
                      −{it.discount_pct}%
                    </span>
                  )}
                  {renderPreview(it)}
                  <div className="font-display font-bold text-[11px] text-center leading-tight">{it.name}</div>
                  {it.active ? (
                    <span className="text-[9px]" style={{ color: "#FFD700" }}>✓ EQUIPPED</span>
                  ) : it.owned ? (
                    <span className="text-[9px] text-white/55">Tap to equip</span>
                  ) : it.stripe_only ? (
                    <span className="text-[9px] text-white/55">🔒 Gem Store</span>
                  ) : it.featured ? (
                    <span className="text-[9px] flex items-center gap-1">
                      <span className="text-white/40 line-through">◈ {it.original_price_gems}</span>
                      <span style={{ color: "#FF6BAA", fontWeight: 800 }}>◈ {it.price_gems}</span>
                    </span>
                  ) : (
                    <span className="text-[9px]" style={{ color: "#00E5FF" }}>◈ {it.price_gems}</span>
                  )}
                </button>
              );
            })}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
